import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Hotel } from '../interfaces/hotel';
import { HotelService } from './hotel.service';
import { HotelLabel, InternalNote, ChannelManager, HotelArchiveHistoryEntry, DiscountRule } from '../interfaces/hotel-settings';

interface WeekendDealRule {
  id?: number;
  start_date?: string;
  end_date?: string;
  minimum_stay?: number;
  rooms?: number[];
  deal_days?: Record<string, boolean>;
}

interface HotelSettingsData {
  discounted_nights?: number | null;
  discount_rules?: DiscountRule[];
  labels?: HotelLabel[];
  notes?: InternalNote[];
  weekend_deal_list?: WeekendDealRule[];
  weekend_deals?: boolean;
  is_archived?: boolean;
  archive_history?: HotelArchiveHistoryEntry[];
  has_future_bookings?: boolean;
  [key: string]: unknown;
}

export interface HotelSettingsApiResponse {
  data: HotelSettingsData;
  message?: string;
}

export interface ArchiveHotelApiResponse {
  status: string;
  message: string;
  data: { is_archived: boolean };
}

export interface LabelsApiResponse {
  data: HotelLabel[];
  message?: string;
}

export interface ChannelManagersApiResponse {
  data: ChannelManager[];
  message?: string;
}

export interface DeleteNoteApiResponse {
  success: boolean;
  message?: string;
}

export interface MlosRule {
  id: number;
  hotel_id: number;
  room_id: number | null;
  rooms: number[];
  start_date: string;
  end_date: string;
  minimum_stay: number;
  deal_days: Record<string, boolean>;
  is_default: number;
}

export interface MlosSettingsApiResponse {
  status: string;
  message: string;
  data: MlosRule[];
}

@Injectable({
  providedIn: 'root'
})
export class HotelSettingService {
  private selectedHotelSubject = new BehaviorSubject<Hotel | null>(null);
  selectedHotel$ = this.selectedHotelSubject.asObservable();
  public selectedHotelId = 0;

  // Default value: empty object (will store process_by_admin, hide_hotel, etc.)
  private hotelStatusSubject = new BehaviorSubject<Record<string, unknown>>({});
  hotelStatus$ = this.hotelStatusSubject.asObservable();

  // Caches for reference data
  private labelsCache$: Observable<LabelsApiResponse> | null = null;
  private channelManagersCache$: Observable<ChannelManagersApiResponse> | null = null;

  constructor(
    private http: HttpService,
    private hotelService: HotelService
  ) { }

  getHotelSettings(): Observable<HotelSettingsApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.get<HotelSettingsApiResponse>(`v2/hotels/settings/${selectedHotelId}`);
  }

  saveHotelSettings(data: Record<string, unknown>): Observable<HotelSettingsApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.put<HotelSettingsApiResponse>(`v2/hotels/settings/${selectedHotelId}`, data);
  }

  /** Archive the current hotel. Super Admin only — enforced server-side. */
  archiveHotel(reason: string): Observable<ArchiveHotelApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.post<ArchiveHotelApiResponse>(`v2/hotels/settings/${selectedHotelId}/archive`, { reason });
  }

  /** Restore the current hotel from the archive. Super Admin only — enforced server-side. */
  restoreHotel(): Observable<ArchiveHotelApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.post<ArchiveHotelApiResponse>(`v2/hotels/settings/${selectedHotelId}/restore`, {});
  }

  /**
   * Fetch MLOS (Minimum Length of Stay) settings from the dedicated endpoint.
   */
  getMlosSettings(): Observable<MlosSettingsApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.get<MlosSettingsApiResponse>(`v2/hotels/${selectedHotelId}/mlos`);
  }

  /**
   * Save MLOS (Minimum Length of Stay) settings using the dedicated endpoint.
   * This endpoint only requires MLOS-related fields.
   */
  saveMlosSettings(data: Record<string, unknown>): Observable<HotelSettingsApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.put<HotelSettingsApiResponse>(`v2/hotels/${selectedHotelId}/mlos`, data);
  }

  deleteInternalNote(nid: number): Observable<DeleteNoteApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.delete<DeleteNoteApiResponse>(`v2/hotels/settings/${selectedHotelId}/notes/${nid}`);
  }

  /**
   * Delete one or more MLOS rules by their IDs.
   * Uses DELETE /api/v2/hotels/settings/{hotelId}/mlos with { delete_ids: [...] } payload.
   */
  deleteMlosRules(deleteIds: number[]): Observable<DeleteNoteApiResponse> {
    const selectedHotelId = this.hotelService.hotelId;
    return this.http.delete<DeleteNoteApiResponse>(
      `v2/hotels/${selectedHotelId}/mlos`,
      { body: { delete_ids: deleteIds } }
    );
  }

  getLabelsList(): Observable<LabelsApiResponse> {
    if (!this.labelsCache$) {
      this.labelsCache$ = this.http.get<LabelsApiResponse>(`v2/labels`).pipe(
        shareReplay(1)
      );
    }
    return this.labelsCache$;
  }

  getChannelManagersList(): Observable<ChannelManagersApiResponse> {
    if (!this.channelManagersCache$) {
      this.channelManagersCache$ = this.http.get<ChannelManagersApiResponse>(`v2/channel-managers`).pipe(
        shareReplay(1)
      );
    }
    return this.channelManagersCache$;
  }

  /** Update hotel settings status (process_by_admin, hide_hotel, etc.) */
  setHotelStatus(settings: Record<string, unknown>): void {
    this.hotelStatusSubject.next(settings); // emit the full object
  }

  bulkDeleteAvailabilities(hotelId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `v2/hotels/${hotelId}/availability/bulk/delete`,
      {}
    );
  }

  /** Return the full hotel status object */
  getHotelStatus(): Record<string, unknown> {
    return this.hotelStatusSubject.getValue();
  }

  /**
   * Invalidate reference data caches
   */
  invalidateCache(): void {
    this.labelsCache$ = null;
    this.channelManagersCache$ = null;
  }
}
